import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { domainCheck } from '~/api/auth';
import AuthenticationStore from '~/auth/authenticationStore';
import { isPreview } from '~/dev/utils/isPreview';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { LoginPage as EdsLoginPage } from '~/eds/src/modules/LoginPage/LoginPage';
import { LoginViewType } from '~/enums/LoginViewType';
import { QueryParamType } from '~/enums/QueryParamType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { getClientFromUrl, getRawClientFromUrl } from '~/routing/utils';
import { withRouting } from '~/routing/withRouting';
import { getAbsoluteRedirectUrl } from '~/services/redirects';

import {
  getPopupModeInfo,
  handlePopupLoginSuccess,
  handlePopupLoginSuccessWhenReady,
} from './popupLoginUtils';
import { useAegisLoginProps } from './useAegisLoginProps';
import { useRolodexLoginProps } from './useRolodexLoginProps';

const AS = AuthenticationStore();

const componentStyles = {
  page: {
    alignItems: 'flex-start',
    backgroundColor: 'inverse.background.secondary',
    display: 'flex',
    height: '100vh',
    margin: 'auto',
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'fixed',
    width: '100vw',
  },
  container: {
    flex: 'auto',
    padding: 4,
    margin: 'auto',
    maxWidth: '1280px',
  },
};

function Page(props) {
  const intl = useIntl();
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);

  const [stage, setStage] = useState(LoginViewType.Domain);
  const [error, setError] = useState();
  const [returnTo, setRedirectTo] = useState(null);

  const [ssoConfigs, setSsoConfigs] = useState(null);
  const [workspace, setWorkspace] = useState('');

  const [enableOidc, setEnableOidc] = useState(false);
  const [enableRolodex, setEnableRolodex] = useState(false);

  const isEmbed = AS.isEmbed();

  // Check if we're in popup mode (e.g., from Word add-in using Office Dialog API)
  // Intentionally captured once at mount - even if URL changes during auth flows,
  // the popup context remains the same for the entire session
  const [isPopupMode] = useState(() => getPopupModeInfo().isPopupMode);

  // enable auto-login if in embedded login mode and ssoConfigs exists or if rolodex enabled for oidc
  const isSsoOnly = (isEmbed && ssoConfigs) || (enableRolodex && enableOidc);

  if (stage === LoginViewType.LoggedIn) {
    // In popup mode, use Office Dialog API to send credentials back
    if (isPopupMode) {
      handlePopupLoginSuccess();
    } else {
      let redirectWorkspace = isPreview
        ? getRawClientFromUrl()
        : workspace.toLocaleLowerCase();

      window.location.href = returnTo
        ? `/${redirectWorkspace}${returnTo}`
        : `/${redirectWorkspace}/`;
    }
  }

  const shouldUseSearchAsHomePage = useFlag(FlagType.SearchAsHomePage);
  if (stage === LoginViewType.LoggedInWithWelcome) {
    // In popup mode, use Office Dialog API to send credentials back
    if (isPopupMode) {
      handlePopupLoginSuccess();
    } else if (shouldUseSearchAsHomePage) {
      window.location.href = `/${workspace.toLowerCase()}/search?welcomeMessage=true`;
    } else {
      window.location.href = `/${workspace.toLowerCase()}/?welcomeMessage=true`;
    }
  }

  const checkClient = (ws) => {
    const fromLocation = props.location.state?.from;
    const formData = fromLocation
      ? {
          client: ws,
          return_to: getAbsoluteRedirectUrl(
            window.location.origin,
            ws.toLowerCase(),
            fromLocation,
          ),
        }
      : { client: ws };

    setIsCheckingDomain(true);
    return domainCheck(formData)
      .then((response) => {
        if (response) {
          if (!props.location.search.includes(QueryParamType.SamlError)) {
            setStage(LoginViewType.Email);
          }
          if (response.sso.error) {
            setError(
              intl.formatMessage(
                {
                  id: 'LoginPage.invalidSsoConfiguration',
                  defaultMessage: 'Invalid SSO Configuration: {error}',
                  description:
                    'Error message for invalid SSO configuration. Example: error = "Missing SAML endpoint"',
                },
                { error: response.sso.error },
              ),
            );
          } else if (response.oidc.error) {
            setError(
              intl.formatMessage(
                {
                  id: 'LoginPage.invalidOidcConfiguration',
                  defaultMessage: 'Invalid SSO Configuration: {error}',
                  description:
                    'Error message for invalid OIDC configuration. Example: error = "Invalid client ID"',
                },
                { error: response.oidc.error },
              ),
            );
          } else {
            setError(null);
          }

          if (response.sso.enabled) {
            setSsoConfigs(response.sso);
          }

          setEnableRolodex(response.enableRolodex);
          setEnableOidc(response.oidc.enabled);
        }
        setIsCheckingDomain(false);
      })
      .catch(() => {
        setError(
          intl.formatMessage({
            id: 'LoginPage.genericError',
            defaultMessage: 'An error occurred. Please check again.',
            description: 'Generic error message when domain check fails',
          }),
        );
      })
      .finally(() => {
        setIsCheckingDomain(false);
      });
  };

  /**
   * Mapping of old/DEPRECATED LoginViewType to new LoginPage.Stage
   */
  const handleChangeWorkspace = (ws) => {
    setWorkspace(ws);
    checkClient(ws);
  };

  const queryChangeWorkspace = [
    handleChangeWorkspace,
    ssoConfigs ? { error: ssoConfigs.error, isSuccess: true } : {},
  ];

  useEffect(() => {
    const isLoggedIn = AS.isLoggedIn();
    const hasReturnTo =
      props.location && props.location.state && props.location.state.from;

    const subdomain = getClientFromUrl();

    // Handle popup mode for already-authenticated users BEFORE any redirect
    // This intercepts the normal "already logged in" redirect flow
    if (isLoggedIn && isPopupMode) {
      handlePopupLoginSuccessWhenReady();
      return; // Don't proceed with normal flow
    }

    if (isLoggedIn && !isEmbed) {
      return props.navigate('/');
    }
    if (hasReturnTo) {
      const { pathname, search } = props.location.state.from;
      if (search) {
        setRedirectTo(`${pathname}/${search}`);
      } else {
        setRedirectTo(`${pathname}`);
      }
    }

    if (subdomain) handleChangeWorkspace(subdomain);
  }, [props.location.search, props.navigate, isPopupMode]);

  // Move aegis related props to useAegisLoginProps and remove them at once
  // when migration to rolodex is done
  const aegisLoginPageProps = useAegisLoginProps({
    ...props,
    workspace,
    ssoConfigs,
    stage,
    setStage,
    error,
    setError,
  });

  const rolodexLoginPageProps = useRolodexLoginProps({
    ...props,
    stage,
    workspace,
    enableOidc,
    setStage,
    error,
    setError,
    isPopupMode,
  });

  if (isSsoOnly) return null;

  return (
    <Box styles={componentStyles.page}>
      <Box styles={componentStyles.container}>
        <ContentContainer loadingContent={{ isLoading: isCheckingDomain }}>
          <EdsLoginPage
            {...aegisLoginPageProps}
            {...rolodexLoginPageProps}
            workspace={workspace}
            isSsoOnly={isSsoOnly}
            enableRolodex={enableRolodex}
            enableOidc={enableOidc}
            queryChangeWorkspace={queryChangeWorkspace}
          />
        </ContentContainer>
      </Box>
    </Box>
  );
}

export default withRouting(Page);
